import { ErrorMessage } from '@/components/Elements/ErrorMessage';
import { ReadOnlyField } from '@/components/Form/ReadOnlyField';
import { type AuthUser } from '@/features/auth/types';
import { ProfileBasicsFields } from '@/features/profile/components/ProfileBasicsFields';
import { ProfileImagesInput } from '@/features/profile/components/ProfileImagesInput';
import { useUser } from '@/lib/auth';
import { useTranslation } from 'react-i18next';
import { EmailChangeSection } from './EmailChangeSection';
import { useUserProfileForm } from './useUserProfileForm';

export type UserProfileFormController = ReturnType<typeof useUserProfileForm>;

const EMAIL_CHANGE_PROVIDERS = new Set(['email', 'oidc']);

type UserProfileFieldsProps = {
  user: AuthUser;
  form: UserProfileFormController;
  disabled?: boolean;
  onEdit?: () => void;
};

export const UserProfileFields = ({
  user,
  form,
  disabled = false,
  onEdit,
}: UserProfileFieldsProps) => {
  const { t } = useTranslation();
  const canChangeEmail =
    Boolean(user.email) &&
    (user.provider === undefined || EMAIL_CHANGE_PROVIDERS.has(user.provider));

  return (
    <div className="space-y-4">
      <ProfileImagesInput
        profileImageUrl={form.profileImage.previewUrl}
        coverImageUrl={form.coverImage.previewUrl}
        description={t('PNG / JPEG / GIF / WebP に対応しています。')}
        accept={form.profileImageAccept}
        profileErrors={form.fieldErrors.profileImageSignedBlobId}
        coverErrors={form.fieldErrors.coverImageSignedBlobId}
        disabled={disabled}
        onProfileSelect={(file) => {
          onEdit?.();
          form.profileImage.selectFile(file);
        }}
        onCoverSelect={(file) => {
          onEdit?.();
          form.coverImage.selectFile(file);
        }}
        onCoverRemove={() => {
          onEdit?.();
          form.coverImage.remove();
        }}
      />

      {canChangeEmail ? (
        <EmailChangeSection currentEmail={user.email} />
      ) : (
        <ReadOnlyField
          label={t('メールアドレス')}
          value={user.email ?? ''}
          appearance="design-system"
        />
      )}

      <ProfileBasicsFields
        firstName={form.firstName}
        lastName={form.lastName}
        language={form.language}
        fieldErrors={form.fieldErrors}
        disabled={disabled}
        appearance="design-system"
        onFirstNameChange={(value) => {
          onEdit?.();
          form.setFirstName(value);
        }}
        onLastNameChange={(value) => {
          onEdit?.();
          form.setLastName(value);
        }}
        onLanguageChange={(value) => {
          onEdit?.();
          form.setLanguage(value);
        }}
      />

      <ErrorMessage errors={form.globalErrors} />
    </div>
  );
};

// Temporary compatibility wrapper until the page-level unified form owns both
// controllers. It intentionally adds no form element or save action.
const UserProfileFormContent = ({ user }: { user: AuthUser }) => (
  <UserProfileFields user={user} form={useUserProfileForm(user)} />
);

export const UserProfileForm = () => {
  const { data: user } = useUser();

  if (!user) return null;

  return (
    <UserProfileFormContent
      key={[
        user.id,
        user.firstName,
        user.lastName,
        user.language,
        user.profileImageUrl,
        user.coverImageUrl,
      ].join(':')}
      user={user}
    />
  );
};
